import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Radar, Loader2, RefreshCw, Settings, Bell, ExternalLink, Import, Filter } from 'lucide-react';
import { toast } from 'sonner';

const CPV_HINTS = {
  '45233120': 'Constructii drumuri', '45233140': 'Lucrari de drumuri', '45233142': 'Reparare drumuri',
  '45233222': 'Pavare si asfaltare', '34922100': 'Marcaje rutiere', '45233141': 'Intretinere drumuri',
  '45233139': 'Intretinere drumuri nationale', '45233220': 'Imbracare suprafete drumuri',
  '45233223': 'Reasfaltare', '45233150': 'Intretinere trotuare',
};

export default function SeapRadarPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [cpvText, setCpvText] = useState('');
  const [minValue, setMinValue] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const [countyFilter, setCountyFilter] = useState('');
  const [minValFilter, setMinValFilter] = useState('');
  const [importingId, setImportingId] = useState('');

  const load = async () => {
    try {
      const r = await API.get('/seap-alerts');
      setConfig(r.data.config); setAlerts(r.data.alerts); setUnread(r.data.unread);
      setCpvText((r.data.config.cpv_codes || []).join(', '));
      setMinValue(r.data.config.min_value || 0);
      setEnabled(r.data.config.enabled !== false);
    } catch { toast.error('Eroare incarcare'); }
  };
  useEffect(() => { load(); }, []);

  const scanNow = async () => {
    setScanning(true);
    try { const r = await API.post('/seap-alerts/scan-now'); toast.success(`Scanare completa: ${r.data.new ?? 0} rezultate noi`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare scanare'); }
    finally { setScanning(false); }
  };

  const saveConfig = async () => {
    try {
      const cpv_codes = cpvText.split(',').map(s => s.trim()).filter(Boolean);
      await API.put('/seap-alerts/config', { cpv_codes, min_value: Number(minValue) || 0, enabled });
      toast.success('Configurare salvata'); load();
    } catch { toast.error('Eroare salvare'); }
  };

  const markRead = async (id) => { try { await API.post(`/seap-alerts/${id}/read`); load(); } catch {} };

  const importAlert = async (a) => {
    setImportingId(a.id);
    try {
      const res = await API.post('/seap/import', { title: a.name, url: a.url, module_type: 'ofertant', procurement_type: 'licitatie_deschisa', estimated_value: a.value || 0, deadline: a.deadline });
      toast.success('Licitatie importata ca proiect!');
      navigate(`/projects/${res.data.id}`);
    } catch { toast.error('Eroare la import'); }
    finally { setImportingId(''); }
  };

  const filteredAlerts = alerts.filter(a => {
    const c = countyFilter.trim().toLowerCase();
    const okCounty = !c || (a.authority || '').toLowerCase().includes(c) || (a.name || '').toLowerCase().includes(c);
    const okVal = !minValFilter || (a.value || 0) >= Number(minValFilter);
    return okCounty && okVal;
  }).sort((x, y) => (y.scor || 0) - (x.scor || 0));

  return (
    <div className="p-8" data-testid="seap-radar-page">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Radar className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Radar SEAP Automat</h1>
          {unread > 0 && <Badge className="bg-rose-500 text-white text-[10px]">{unread} noi</Badge>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Reincarca</Button>
          <Button size="sm" onClick={scanNow} disabled={scanning} data-testid="scan-now-btn">
            {scanning ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Radar className="w-3.5 h-3.5 mr-1.5" />}
            {scanning ? 'Se scaneaza...' : 'Scaneaza acum'}
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Scanare automata de doua ori pe zi (07:00 si 13:00) pe SEAP (e-licitatie.ro) dupa codurile CPV monitorizate. Se pastreaza 48h. Doar licitatii la care se poate participa.
        {config?.last_scan && <span className="ml-1">Ultima scanare: {new Date(config.last_scan).toLocaleString('ro-RO')}</span>}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Config */}
        <Card className="bg-card border-white/10 lg:col-span-1 h-fit">
          <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Settings className="w-4 h-4 text-amber-400" />Configurare Monitorizare</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground">Coduri CPV (separate prin virgula)</label>
              <Input value={cpvText} onChange={e => setCpvText(e.target.value)} className="mt-1 bg-zinc-900/50 border-white/10 text-xs font-mono-data" data-testid="cpv-input" />
              <div className="flex flex-wrap gap-1 mt-2">
                {cpvText.split(',').map(c => c.trim()).filter(Boolean).map(c => (
                  <Badge key={c} variant="outline" className="text-[9px] bg-primary/5 text-primary border-0">{c} · {CPV_HINTS[c] || ''}</Badge>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Valoare minima (RON)</label>
              <Input type="number" value={minValue} onChange={e => setMinValue(e.target.value)} className="mt-1 bg-zinc-900/50 border-white/10 text-xs font-mono-data" data-testid="minvalue-input" />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs text-muted-foreground">Scanare automata activa</label>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="enabled-switch" />
            </div>
            <Button onClick={saveConfig} className="w-full" size="sm" data-testid="save-config-btn">Salveaza configurarea</Button>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="bg-card border-white/10 lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Bell className="w-4 h-4 text-primary" />Licitatii Deschise ({filteredAlerts.length})</CardTitle>
            <div className="flex items-center gap-2 mt-2">
              <Filter className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <Input value={countyFilter} onChange={e => setCountyFilter(e.target.value)} placeholder="Filtreaza dupa judet / autoritate..." className="h-8 text-xs bg-zinc-900/50 border-white/10" data-testid="county-filter" />
              <Input type="number" value={minValFilter} onChange={e => setMinValFilter(e.target.value)} placeholder="Valoare min. RON" className="h-8 text-xs bg-zinc-900/50 border-white/10 w-40 font-mono-data" data-testid="minval-filter" />
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {filteredAlerts.map(a => (
              <div key={a.id} className={`p-3 rounded-lg border ${a.read ? 'bg-zinc-900/30 border-white/5' : 'bg-primary/5 border-primary/20'}`} data-testid={`seap-alert-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{a.name}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {typeof a.scor === 'number' && <Badge className={`text-[9px] ${a.scor >= 70 ? 'bg-emerald-500 text-white' : a.scor >= 50 ? 'bg-amber-500 text-black' : 'bg-zinc-600 text-white'}`} data-testid={`seap-score-${a.id}`}>Sanse {a.scor}%</Badge>}
                      <Badge variant="outline" className="text-[9px] bg-blue-500/10 text-blue-400 border-0">{a.cpv} · {a.cpv_label}</Badge>
                      {a.value > 0 && <span className="text-[10px] text-emerald-400 font-mono-data">{a.value.toLocaleString()} RON</span>}
                      {a.deadline && <span className="text-[10px] text-amber-400 font-mono-data">Termen: {String(a.deadline).slice(0, 10)}</span>}
                      {a.authority && <span className="text-[10px] text-muted-foreground truncate max-w-[220px]">{a.authority}</span>}
                      {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline flex items-center gap-0.5" data-testid={`seap-alert-link-${a.id}`}>Deschide <ExternalLink className="w-2.5 h-2.5" /></a>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => importAlert(a)} disabled={importingId === a.id} className="h-7 text-[10px]" data-testid={`seap-import-${a.id}`}>
                      {importingId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Import className="w-3 h-3 mr-1" />Importa</>}
                    </Button>
                    {!a.read && <button onClick={() => markRead(a.id)} className="text-[10px] text-primary hover:underline">Citit</button>}
                  </div>
                </div>
              </div>
            ))}
            {!filteredAlerts.length && <p className="text-xs text-muted-foreground text-center py-8">{alerts.length ? 'Niciun rezultat pentru filtrul aplicat.' : 'Nicio licitatie deschisa detectata inca. Apasa "Scaneaza acum" sau asteapta scanarea automata (07:00 si 13:00).'}</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
